import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/shared";
import ExportSubmissionsModal from "../components/shared/export-submissions-modal";
import SubmissionEditForm from "../components/shared/submission-edit-form";
import {
  collection,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  updateDoc,
  QueryDocumentSnapshot,
  DocumentData
} from "firebase/firestore";
import { auth, db } from "../firebase";
import {
  Table,
  Loader,
  Button,
  Text,
  Divider,
  Space,
  Select,
  Drawer,
  Alert,
  Group,
  Popover,
  Modal,
  ScrollArea,
  TextInput,
  Badge,
  SegmentedControl,
  ActionIcon,
  Menu,
  Tooltip,
  Checkbox,
} from "@mantine/core";
import { DateRangePicker } from "@mantine/dates";
import { useMediaQuery } from "@mantine/hooks";
import {
  extractFilterOptionsForField,
  filterSubmissions,
} from "../utils/submission-filter-utils";
import { ArrowBack, Bolt, ChevronDown, ChevronUp, Download, Eye, Trash, Edit, EyeCheck, Filter, Copy, Check } from "tabler-icons-react";
import { IconDotsVertical, IconX } from "@tabler/icons-react";
import {
  getWeekOldDate,
  isValidDocumentNumber,
  getSingleDayDateRange,
  normalizeUppercaseValue,
  DOCUMENT_NUMBER_ERROR_MESSAGE,
} from "../components/listings/hooks";
import SubmissionFieldDisplayTable, { FieldValueDisplay } from "../components/shared/submission-field-display";
import {
  useBuildQuery,
  useHandleQueryResult,
  useResetListingState,
} from "../components/listings/hooks/use-listing-query";
import PDFExportButton from "../components/shared/pdf-export-button";
import { DateRangeQuickPills } from "../components/shared/date-range-quick-pills";
import { alterTemplateSubmission, deleteTemplateSubmission } from "../components/shared/hooks/data-functions";
import {
  booleanParam,
  dateParam,
  enumParam,
  stringParam,
  upperStringParam,
  useUrlOverlayState,
  useUrlState,
} from "../hooks/useUrlState";
import { FlexFormTemplate, FlexFormFirestoreSelectOptionSet, TemplateSubmission } from "@captiva/shared-types";
import VerifiedIcon from "../components/listings/verified-icon";
import { ResponsiveDataTable } from "../components/listings/responsive-data-table";

export interface ExtendedTemplateSubmission extends TemplateSubmission {
  id: string;
  documentNumber?: string;
  submissionDate?: any; // firebase Timestamp or string/Date
  transactionReviewed?: boolean;
}

function toTimestampToDateString(value: any): string {
  if (value && typeof value === "object" && ("seconds" in value || "_seconds" in value)) {
    const secs = value.seconds || value._seconds || 0;
    return (
      new Date(secs * 1000).toLocaleDateString("en-GB") +
      " " +
      new Date(secs * 1000).toLocaleTimeString("en-GB")
    );
  } else if (value) {
    return new Date(value).toLocaleDateString("en-GB") || "Unknown";
  }
  return "Unknown";
}

export interface FormTemplateSubmissionsProps {
  companyId: string;
}

export default function FormTemplateSubmissions({ companyId }: FormTemplateSubmissionsProps) {
  const [templates, setTemplates] = useState<FlexFormTemplate[]>([]);
  const [submissions, setSubmissions] = useState<ExtendedTemplateSubmission[]>([]);
  const [doneInitialFetch, setInitialFetched] = useState<boolean>(false);
  const [lastVisibleDoc, setLastVisibleDoc] = useState<QueryDocumentSnapshot<DocumentData> | undefined>(undefined);
  const [paginationDone, setPaginationDone] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [drawerSubmission, setDrawerSubmission] = useState<ExtendedTemplateSubmission | null>(null);
  const [actionsOpened, setActionsOpened] = useState<boolean>(false);
  const [selectOptions, setSelectOptions] = useState<FlexFormFirestoreSelectOptionSet[]>([]);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState<boolean>(false);
  const [documentNumberError, setDocumentNumberError] = useState<string>("");
  const [deepLinkError, setDeepLinkError] = useState<string>("");
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState<number>(20);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const isMobile = useMediaQuery('(max-width: 768px)');
  const columnLimit = 5;
  const defaultFromDate = useMemo(() => getWeekOldDate(1), []);
  const defaultToDate = useMemo(() => new Date(), []);
  const urlSchema = useMemo(
    () => ({
      templateId: stringParam(null),
      template: stringParam(null),
      from: dateParam(null),
      to: dateParam(null),
      allTime: booleanParam(false),
      doc: upperStringParam(null),
      submissionId: stringParam(null),
      mode: enumParam(["view", "edit", "delete", "export"] as const, null),
      filterField: stringParam(null),
      filterValue: stringParam(null),
    }),
    []
  );
  const [urlState, setUrlState] = useUrlState(urlSchema);
  const {
    selectedId: selectedSubmissionId,
    mode: overlayMode,
    openOverlay,
    closeOverlay,
  } = useUrlOverlayState({
    idKey: "submissionId",
    modeKey: "mode",
    defaultMode: "view",
    state: urlState,
    setState: setUrlState,
  });

  const selectedTemplate = urlState.templateId || urlState.template || null;
  const hasExplicitDateRange = Boolean(urlState.from || urlState.to);
  const dates = useMemo((): [Date | null, Date | null] => {
    if (hasExplicitDateRange) {
      return [urlState.from, urlState.to];
    }
    return [defaultFromDate, defaultToDate];
  }, [defaultFromDate, defaultToDate, hasExplicitDateRange, urlState.from, urlState.to]);

  const dateRangeKey = `${dates[0] instanceof Date ? dates[0].toISOString() : ""}|${dates[1] instanceof Date ? dates[1].toISOString() : ""}`;
  const stableDates = useMemo(() => dates, [dateRangeKey]);
  const documentNumber = urlState.doc || "";
  const [copiedDocNumber, setCopiedDocNumber] = useState(false);
  const documentNumberKey = documentNumber || "";
  const selectedTemplateKey = selectedTemplate || "";
  const effectiveMode = selectedSubmissionId ? overlayMode || "view" : overlayMode;
  const drawerOpen = Boolean(selectedSubmissionId) && effectiveMode !== "export";
  const deleteModalOpen = Boolean(selectedSubmissionId) && effectiveMode === "delete";
  const editMode = effectiveMode === "edit";
  const hasValidDocumentNumber = documentNumber && isValidDocumentNumber(documentNumber);
  const hadSelectedSubmissionRef = useRef<boolean>(false);
  const hasNegativeBalance = creditBalance !== null && creditBalance < 0;
  const exportModalOpen = effectiveMode === "export" && !hasNegativeBalance;

  // Get template fields for table headers
  const selectedTemplateObj = templates.find((t) => t.id === selectedTemplate);
  const displayFields = useMemo(() =>
    selectedTemplateObj
      ? (selectedTemplateObj.fields || []).slice(0, columnLimit)
      : [],
    [selectedTemplateObj, columnLimit]
  );

  const filterField = urlState.filterField || null;
  const filterValue = urlState.filterValue || null;

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (documentNumber && documentNumber.trim()) count++;
    if (filterValue && String(filterValue).trim()) count++;
    if (urlState.allTime) count++;
    else if (hasExplicitDateRange) count++;
    return count;
  }, [documentNumber, filterValue, hasExplicitDateRange, urlState.allTime]);

  const currentFilterFieldObj = useMemo(() => {
    if (!selectedTemplateObj || !filterField || filterField === "__ALL__") return null;
    return (selectedTemplateObj.fields || []).find((f) => f.key === filterField) || null;
  }, [selectedTemplateObj, filterField]);

  const availableFieldOptions = useMemo(() => {
    if (!currentFilterFieldObj) return [];
    return extractFilterOptionsForField(currentFilterFieldObj, selectOptions, submissions);
  }, [currentFilterFieldObj, selectOptions, submissions]);

  const fieldSelectData = useMemo(() => {
    if (!selectedTemplateObj) return [];
    const fields = selectedTemplateObj.fields || [];
    return [
      { value: "__ALL__", label: "All fields" },
      ...fields.map((f) => ({
        value: f.key,
        label: f.label || f.key,
      })),
    ];
  }, [selectedTemplateObj]);

  const filteredSubmissions = useMemo(() => {
    return filterSubmissions(submissions, filterField, filterValue, selectedTemplateObj?.fields || []);
  }, [submissions, filterField, filterValue, selectedTemplateObj]);

  const handleSort = (colKey: string) => {
    if (sortColumn === colKey) {
      if (sortDirection === "asc") {
        setSortDirection("desc");
      } else {
        setSortColumn(null);
        setSortDirection("asc");
      }
    } else {
      setSortColumn(colKey);
      setSortDirection("asc");
    }
  };

  const sortedAndFilteredSubmissions = useMemo(() => {
    if (!sortColumn) return filteredSubmissions;

    const sorted = [...filteredSubmissions];
    const modifier = sortDirection === "asc" ? 1 : -1;

    sorted.sort((a: any, b: any) => {
      let valA: any;
      let valB: any;

      if (sortColumn === "docNumber") {
        valA = a.documentNumber;
        valB = b.documentNumber;
      } else if (sortColumn === "date") {
        const getDateMs = (ts: any) => {
          if (!ts) return 0;
          if (typeof ts.toMillis === "function") return ts.toMillis();
          if (typeof ts.toDate === "function") return ts.toDate().getTime();
          if (ts instanceof Date) return ts.getTime();
          if (typeof ts === "number") return ts;
          return new Date(ts).getTime() || 0;
        };
        return (getDateMs(a.submissionDate) - getDateMs(b.submissionDate)) * modifier;
      } else {
        valA = a.data?.[sortColumn]?.value;
        valB = b.data?.[sortColumn]?.value;
      }

      const isNullA = valA === undefined || valA === null || valA === "";
      const isNullB = valB === undefined || valB === null || valB === "";
      if (isNullA && isNullB) return 0;
      if (isNullA) return 1;
      if (isNullB) return -1;

      const numA = Number(valA);
      const numB = Number(valB);
      if (!isNaN(numA) && !isNaN(numB) && typeof valA !== "boolean" && typeof valB !== "boolean") {
        return (numA - numB) * modifier;
      }

      const strA = String(valA);
      const strB = String(valB);
      return strA.localeCompare(strB, undefined, { numeric: true, sensitivity: "base" }) * modifier;
    });

    return sorted;
  }, [filteredSubmissions, sortColumn, sortDirection]);

  const handlePageSizeChange = (newPageSize: number) => {
    if (newPageSize === pageSize) return;
    setPageSize(newPageSize);
    reset();
  };

  // Fetch templates
  useEffect(() => {
    if (!companyId) return;
    const fetchTemplates = async () => {
      const q = query(
        collection(db, "flexFormTemplates"),
        where("companyId", "==", companyId),
        where("archived", "!=", true)
      );
      const snapshot = await getDocs(q);
      const list = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FlexFormTemplate);
      list.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setTemplates(list);
    };
    fetchTemplates();
  }, [companyId]);

  // Fetch credits
  useEffect(() => {
    if (!companyId) return;
    let active = true;
    const fetchCredits = async () => {
      try {
        const docRef = doc(db, "credits", companyId);
        const docSnap = await getDoc(docRef);
        if (active) {
          if (docSnap.exists()) {
            setCreditBalance(docSnap.data()?.creditBalance ?? 0);
          } else {
            setCreditBalance(0);
          }
        }
      } catch (error) {
        console.error("Error fetching credits:", error);
      }
    };
    fetchCredits();
    return () => {
      active = false;
    };
  }, [companyId]);

  // Fetch select options
  const refreshSelectOptions = useCallback(async () => {
    if (!selectedTemplateObj || !companyId) return;
    const firestoreIds = (selectedTemplateObj.fields || [])
      .filter(f => f.optionsSource?.type === 'firestore')
      .map(f => (f.optionsSource as any).id as string)
      .filter((id, index, arr) => arr.indexOf(id) === index);
    if (firestoreIds.length === 0) {
      setSelectOptions([]);
      return;
    }
    const promises = firestoreIds.map(id => getDoc(doc(db, 'flexFormSelectOptions', id)));
    const snapshots = await Promise.all(promises);
    const options = snapshots.map(snap => ({ id: snap.id, ...snap.data() }) as FlexFormFirestoreSelectOptionSet);
    setSelectOptions(options);
  }, [selectedTemplateObj, companyId]);

  useEffect(() => {
    refreshSelectOptions();
  }, [refreshSelectOptions]);

  // Query hooks for pagination
  const buildQuery = useBuildQuery({
    db,
    companyId,
    pageSize,
    collectionName: "flexFormSubmissions",
    dateField: "submissionDate",
  });

  const handleResults = useHandleQueryResult({
    pageSize,
    setDocuments: setSubmissions as any,
    setLastVisibleDoc: setLastVisibleDoc as any,
    setPaginationDone,
  });

  const reset = useResetListingState({
    setDocuments: setSubmissions as any,
    setInitialFetched,
    setLastVisibleDoc: setLastVisibleDoc as any,
    setPaginationDone,
  });

  // Reset when template or company changes
  useEffect(() => {
    reset();
    setDocumentNumberError("");
  }, [selectedTemplateKey, companyId, reset]);

  // Reset listing when date range changes
  useEffect(() => {
    if (!urlState.allTime && selectedTemplate && companyId && stableDates && stableDates[0] && stableDates[1] && stableDates[0] <= stableDates[1]) {
      reset();
    }
  }, [dateRangeKey, selectedTemplateKey, companyId, reset, selectedTemplate, stableDates, urlState.allTime]);

  // Reset listing when allTime changes
  useEffect(() => {
    if (selectedTemplate && companyId) {
      reset();
    }
  }, [urlState.allTime, selectedTemplateKey, companyId, reset, selectedTemplate]);

  // Reset listing when document number changes (valid value or cleared)
  useEffect(() => {
    if (!selectedTemplate || !companyId) return;
    if (documentNumber && !isValidDocumentNumber(documentNumber)) return;
    reset();
  }, [documentNumberKey, selectedTemplateKey, companyId, reset, documentNumber, selectedTemplate]);

  useEffect(() => {
    if (!isMobile) {
      setMobileFiltersOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    if (!selectedSubmissionId) {
      hadSelectedSubmissionRef.current = false;
      setDrawerSubmission(null);
      setDeepLinkError("");
      return;
    }

    hadSelectedSubmissionRef.current = true;

    const existingSubmission = submissions.find((submission) => submission.id === selectedSubmissionId);
    if (existingSubmission) {
      setDrawerSubmission(existingSubmission);
      setDeepLinkError("");
      return;
    }

    if (drawerSubmission?.id === selectedSubmissionId) {
      setDeepLinkError("");
      return;
    }

    let cancelled = false;

    const fetchSubmission = async () => {
      try {
        const snapshot = await getDoc(doc(db, "flexFormSubmissions", selectedSubmissionId));

        if (!snapshot.exists()) {
          if (!cancelled) {
            setDeepLinkError("The linked submission could not be found.");
            closeOverlay({ replace: true });
          }
          return;
        }

        const nextSubmission = { id: snapshot.id, ...snapshot.data() } as ExtendedTemplateSubmission;
        if (cancelled) return;

        setDrawerSubmission(nextSubmission);
        setDeepLinkError("");
        const nextUrlState: any = {};

        if (nextSubmission.templateId && nextSubmission.templateId !== selectedTemplate) {
          nextUrlState.templateId = nextSubmission.templateId;
        }

        if (!urlState.from && !urlState.to) {
          const alignedDateRange = getSingleDayDateRange(nextSubmission.submissionDate);
          if (alignedDateRange) {
            nextUrlState.from = alignedDateRange[0];
            nextUrlState.to = alignedDateRange[1];
          }
        }

        if (Object.keys(nextUrlState).length > 0) {
          setUrlState(nextUrlState, { replace: true });
        }
      } catch (error) {
        if (!cancelled) {
          console.error("Failed to resolve linked submission", error);
          setDeepLinkError("The linked submission could not be opened.");
          closeOverlay({ replace: true });
        }
      }
    };

    fetchSubmission();

    return () => {
      cancelled = true;
    };
  }, [closeOverlay, drawerSubmission, selectedSubmissionId, selectedTemplate, setUrlState, submissions, urlState.from, urlState.to]);

  useEffect(() => {
    if (overlayMode && overlayMode !== "export" && !selectedSubmissionId) {
      setUrlState({ mode: null }, { replace: true });
    }
  }, [overlayMode, selectedSubmissionId, setUrlState]);

  // Load more submissions (pagination)
  const loadMoreEntries = useCallback(
    async (isInitialLoad = false) => {
      if (!selectedTemplate || !companyId) return;
      setLoading(true);
      const activeFilters = {
        templateId: selectedTemplate,
        documentNumber: documentNumber,
      };
      const filterConfig = [
        {
          paramName: "templateId",
          fieldIdToFilter: "templateId",
          label: "Template",
        },
        {
          paramName: "documentNumber",
          fieldIdToFilter: "documentNumber",
          label: "Document #",
        }
      ];
      const q = buildQuery(
        isInitialLoad ? undefined : lastVisibleDoc,
        urlState.allTime ? null : stableDates,
        activeFilters,
        filterConfig
      );
      const snapshot = await getDocs(q);
      handleResults(snapshot, isInitialLoad);
      if (isInitialLoad) setInitialFetched(true);
      setLoading(false);
    },
    [selectedTemplate, companyId, buildQuery, stableDates, lastVisibleDoc, handleResults, documentNumber, urlState.allTime]
  );

  const styles = {
    table: {
      minWidth: isMobile ? "100%" : 800,
      borderCollapse: "collapse" as const,
      width: "100%",
      tableLayout: "fixed" as const,
    },
    th: {
      borderBottom: "2px solid #eee",
      padding: "12px 15px",
      textAlign: "left" as const,
      wordWrap: "break-word" as const,
      overflowWrap: "break-word" as const,
    },
    td: {
      borderBottom: "1px solid #eee",
      padding: "12px 15px",
      wordWrap: "break-word" as const,
      overflowWrap: "break-word" as const,
    },
    tr: {},
  };

  // Initial load
  useEffect(() => {
    if (
      selectedTemplate &&
      companyId &&
      (urlState.allTime || (stableDates[0] && stableDates[1])) &&
      !loading &&
      !doneInitialFetch
    ) {
      loadMoreEntries(true);
    }
  }, [
    selectedTemplate,
    companyId,
    urlState.allTime,
    dateRangeKey,
    loading,
    doneInitialFetch,
    loadMoreEntries,
    stableDates,
  ]);

  const handleDelete = async () => {
    if (!drawerSubmission) return;
    try {
      const response = await deleteTemplateSubmission(drawerSubmission.id, companyId);
      if (response && response.code === 200) {
        setSubmissions(prev => prev.filter(s => s.id !== drawerSubmission.id));
        closeOverlay({ replace: true });
        reset();
        loadMoreEntries(true);
        // Option set sync is handled authoritatively by the server deleteRecord endpoint
        refreshSelectOptions().catch(() => {});
      } else {
        console.error("Delete failed");
      }
    } catch (error) {
      console.error("Delete failed", error);
    }
  };

  const handleDocumentNumberChange = (value: string) => {
    const upperValue = normalizeUppercaseValue(value);
    setUrlState({ doc: upperValue || null }, { replace: true });

    if (upperValue && !isValidDocumentNumber(upperValue)) {
      setDocumentNumberError(DOCUMENT_NUMBER_ERROR_MESSAGE);
    } else {
      setDocumentNumberError("");
    }
  };

  const handleAdjustDateRange = () => {
    if (submissions.length === 0) return;

    const resultDateValue = submissions[0]?.submissionDate;
    const alignedDateRange = getSingleDayDateRange(resultDateValue);
    if (!alignedDateRange) return;

    setUrlState(
      {
        from: alignedDateRange[0],
        to: alignedDateRange[1],
      },
      { replace: false }
    );
  };

  const handleDateRangeChange = (range: [Date | null, Date | null]) => {
    const [fromDate, toDate] = range || [];

    setUrlState(
      {
        from: fromDate || null,
        to: toDate || null,
      },
      { replace: true }
    );
  };

  const handleTemplateChange = (templateId: string | null) => {
    setUrlState(
      {
        templateId: templateId || null,
        template: null,
        submissionId: null,
        mode: null,
        filterField: null,
        filterValue: null,
      },
      { replace: false }
    );
  };

  const handleFilterFieldChange = (fieldKey: string | null) => {
    setUrlState(
      {
        filterField: fieldKey || null,
        filterValue: null,
      },
      { replace: true }
    );
  };

  const handleFilterValueChange = (val: string | null) => {
    setUrlState(
      {
        filterValue: val || null,
      },
      { replace: true }
    );
  };

  const handleClearFilter = () => {
    setUrlState(
      {
        filterField: null,
        filterValue: null,
      },
      { replace: true }
    );
  };

  const handleTransactionReviewed = async () => {
    if (!drawerSubmission) return;
    setLoading(true);
    try {
      await updateDoc(doc(db, 'flexFormSubmissions', drawerSubmission.id), { transactionReviewed: true });

      const updatedSubmission = { ...drawerSubmission, transactionReviewed: true };
      setDrawerSubmission(updatedSubmission);

      setSubmissions((prev) => {
        const newDocs = [...prev];
        const index = newDocs.findIndex((d) => d.id === drawerSubmission.id);
        if (index !== -1) {
          newDocs[index] = updatedSubmission;
        }
        return newDocs;
      });
      setActionsOpened(false);
    } catch (error) {
      console.error('Failed to mark transaction as reviewed', error);
    } finally {
      setLoading(false);
    }
  };

  const renderFilterValueInput = (showLabel = false) => {
    if (!filterField) return null;

    if (availableFieldOptions.length > 0) {
      return (
        <Select
          label={showLabel ? "Value" : undefined}
          placeholder="Select option"
          data={availableFieldOptions}
          value={filterValue}
          onChange={handleFilterValueChange}
          style={{ width: isMobile ? "100%" : 180, minWidth: isMobile ? "100%" : 180 }}
          searchable
          clearable
          size="sm"
        />
      );
    }

    return (
      <TextInput
        label={showLabel ? "Value" : undefined}
        placeholder={filterField === "__ALL__" ? "Search all fields..." : "Type to search..."}
        value={filterValue || ""}
        onChange={(e) => handleFilterValueChange(e.currentTarget.value)}
        style={{ width: isMobile ? "100%" : 180, minWidth: isMobile ? "100%" : 180 }}
        size="sm"
        rightSection={
          filterValue ? (
            <ActionIcon
              size="xs"
              color="gray"
              onClick={() => handleFilterValueChange("")}
              aria-label="Clear search"
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
      />
    );
  };

  const renderMobileFilters = () => (
    <>
      <Checkbox
        label="All Time"
        data-testid="all-time-toggle"
        aria-label="All Time"
        checked={Boolean(urlState.allTime)}
        onChange={(e) => {
          setUrlState({ allTime: e.currentTarget.checked }, { replace: true });
        }}
        disabled={!!hasValidDocumentNumber}
        size={isMobile ? "md" : "sm"}
        mb="sm"
      />
      <DateRangePicker
        placeholder="Date range"
        label="Date range"
        dropdownPosition="bottom"
        amountOfMonths={1}
        style={{ width: "100%", opacity: (hasValidDocumentNumber || urlState.allTime) ? 0.5 : 1 }}
        inputFormat="MM/DD/YYYY"
        clearable={false}
        value={dates}
        onChange={handleDateRangeChange}
        disabled={!!hasValidDocumentNumber || Boolean(urlState.allTime)}
        allowSingleDateInRange
        size={isMobile ? "md" : "sm"}
      />
      <DateRangeQuickPills
        value={dates}
        onChange={handleDateRangeChange}
        disabled={!!hasValidDocumentNumber || Boolean(urlState.allTime)}
      />
      {selectedTemplate && (
        <TextInput
          label="Document #"
          placeholder="e.g. T000-000-001"
          value={documentNumber}
          onChange={(e) => handleDocumentNumberChange(e.currentTarget.value)}
          error={documentNumberError}
          style={{ width: "100%" }}
          size="sm"
          rightSection={
            documentNumber ? (
              <ActionIcon
                size="xs"
                color="gray"
                onClick={() => handleDocumentNumberChange("")}
                aria-label="Clear document number"
              >
                <IconX size={14} />
              </ActionIcon>
            ) : null
          }
        />
      )}
      {selectedTemplate && (
        <Select
          label="Filter field"
          placeholder="Choose field"
          data={fieldSelectData}
          value={filterField}
          onChange={handleFilterFieldChange}
          style={{ width: "100%" }}
          searchable
          clearable
          size="sm"
        />
      )}
      {selectedTemplate && renderFilterValueInput(true)}
      {selectedTemplate && filterValue && (
        <Button
          color="gray"
          variant="subtle"
          size="sm"
          onClick={handleClearFilter}
          fullWidth
        >
          Clear
        </Button>
      )}
      {selectedTemplate && (
        <Button
          color="cap-blue"
          variant="outline"
          leftIcon={<Download size={14} />}
          fullWidth
          disabled={hasNegativeBalance}
          onClick={() => setUrlState({ mode: "export" }, { replace: false })}
          size="sm"
        >
          Export
        </Button>
      )}
      {selectedTemplate && hasValidDocumentNumber && submissions.length > 0 && (
        <Button
          color="cap-blue"
          variant="light"
          size="sm"
          fullWidth
          onClick={handleAdjustDateRange}
        >
          Align date range
        </Button>
      )}
    </>
  );

  return (
    <>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          height: "calc(100vh - 105px)",
          minHeight: 0,
        }}
      >
        <div style={{ flexShrink: 0 }}>
          <PageHeader
            title="Form submissions"
            subtitle="View, review, and filter captured mobile submissions"
            action={
              !isMobile ? (
                <div style={{ display: "flex", gap: "0.6rem", alignItems: "center", flexWrap: "wrap" }}>
                  <label htmlFor="flex-form-select" style={{ display: "none" }}>Select form</label>
                  <Select
                    id="flex-form-select"
                    aria-label="Select form"
                    placeholder="Select form"
                    data={templates.map((t) => ({ value: t.id || "", label: t.name || "" }))}
                    value={selectedTemplate}
                    onChange={handleTemplateChange}
                    style={{ width: 230 }}
                    searchable
                    clearable
                    size="sm"
                  />
                  <DateRangePicker
                    placeholder="Date range"
                    aria-label="Date range"
                    dropdownPosition="bottom"
                    amountOfMonths={2}
                    style={{
                      width: 200,
                      opacity: (hasValidDocumentNumber || urlState.allTime) ? 0.5 : 1,
                    }}
                    inputFormat="MM/DD/YYYY"
                    clearable={false}
                    value={dates}
                    onChange={handleDateRangeChange}
                    disabled={!!hasValidDocumentNumber || Boolean(urlState.allTime)}
                    allowSingleDateInRange
                    size="sm"
                  />
                  <Checkbox
                    label="All Time"
                    data-testid="all-time-toggle"
                    aria-label="All Time"
                    checked={Boolean(urlState.allTime)}
                    onChange={(e) => {
                      setUrlState({ allTime: e.currentTarget.checked }, { replace: true });
                    }}
                    disabled={!!hasValidDocumentNumber}
                    size="sm"
                    styles={{ root: { display: "flex", alignItems: "center" } }}
                  />
                  {selectedTemplate && (
                    <Button
                      color="cap-blue"
                      variant="outline"
                      leftIcon={<Download size={14} />}
                      disabled={hasNegativeBalance}
                      onClick={() => setUrlState({ mode: "export" }, { replace: false })}
                      size="sm"
                    >
                      Export
                    </Button>
                  )}
                </div>
              ) : undefined
            }
            alert={
              (deepLinkError || hasNegativeBalance) ? (
                <>
                  {deepLinkError && (
                    <Alert title="Unable to open linked submission" color="yellow" style={{ marginBottom: hasNegativeBalance ? "0.5em" : 0 }}>
                      {deepLinkError}
                    </Alert>
                  )}
                  {hasNegativeBalance && (
                    <Alert title="Export Disabled" color="red">
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
                        <Text size="sm">
                          Export & download of submissions is disabled because your company has a negative credit balance.
                        </Text>
                        <Link to="/dashboard/settings" style={{ textDecoration: "none" }}>
                          <Button color="red" size="sm" variant="outline">
                            Top Up Credits
                          </Button>
                        </Link>
                      </div>
                    </Alert>
                  )}
                </>
              ) : undefined
            }
            withoutDivider
          />

          {isMobile ? (
            <div style={{ width: "100%", display: "grid", gap: "0.75em", marginBottom: "0.75em" }}>
              <Select
                label="Select form"
                placeholder="Select form"
                data={templates.map((t) => ({ value: t.id || "", label: t.name || "" }))}
                value={selectedTemplate}
                onChange={handleTemplateChange}
                style={{ width: "100%" }}
                searchable
                clearable
                size="sm"
              />
              <Button
                variant="outline"
                color="cap-blue"
                fullWidth
                size="sm"
                leftIcon={<Filter size={16} />}
                aria-label={activeFilterCount > 0 ? `Show filters (${activeFilterCount})` : "Show filters"}
                onClick={() => setMobileFiltersOpen(true)}
              >
                Filters
                {activeFilterCount > 0 && (
                  <Badge
                    size="xs"
                    ml={8}
                    color="cap-blue"
                    variant="filled"
                    styles={{
                      root: {
                        minWidth: 20,
                        height: 20,
                        padding: "0 6px",
                        borderRadius: 10,
                        fontSize: 11,
                        fontWeight: 700,
                        textTransform: "none",
                        lineHeight: 1,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                      inner: {
                        lineHeight: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      },
                    }}
                  >
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              <Drawer
                opened={mobileFiltersOpen}
                onClose={() => setMobileFiltersOpen(false)}
                position="bottom"
                size="auto"
                title={
                  <Text weight={600} size="md">
                    Filter Submissions {activeFilterCount > 0 ? `(${activeFilterCount})` : ""}
                  </Text>
                }
                padding="md"
                styles={{
                  drawer: {
                    borderTopLeftRadius: "12px",
                    borderTopRightRadius: "12px",
                    maxHeight: "80vh",
                    overflowY: "auto",
                  },
                }}
              >
                <div style={{ display: "grid", gap: "0.75em", paddingBottom: "max(1.5em, env(safe-area-inset-bottom))" }}>
                  {renderMobileFilters()}
                  <Button color="cap-blue" fullWidth onClick={() => setMobileFiltersOpen(false)}>
                    Apply Filters
                  </Button>
                </div>
              </Drawer>
            </div>
          ) : (
            selectedTemplate && (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  flexWrap: "wrap",
                  gap: "0.6rem",
                  marginBottom: "0.75rem",
                  padding: "0.4rem 0.6rem",
                  backgroundColor: "#f8f9fa",
                  borderRadius: "6px",
                  border: "1px solid #e9ecef",
                }}
              >
                <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                  <TextInput
                    placeholder="e.g. T000-000-001"
                    aria-label="Document #"
                    value={documentNumber}
                    onChange={(e) => handleDocumentNumberChange(e.currentTarget.value)}
                    error={documentNumberError}
                    style={{ width: 150 }}
                    size="sm"
                    rightSection={
                      documentNumber ? (
                        <ActionIcon
                          size="xs"
                          color="gray"
                          onClick={() => handleDocumentNumberChange("")}
                          aria-label="Clear document number"
                        >
                          <IconX size={14} />
                        </ActionIcon>
                      ) : null
                    }
                  />
                  <Select
                    placeholder="Choose field"
                    aria-label="Filter field"
                    data={fieldSelectData}
                    value={filterField}
                    onChange={handleFilterFieldChange}
                    style={{ width: 170 }}
                    searchable
                    clearable
                    size="sm"
                  />
                  {renderFilterValueInput(false)}
                  {hasValidDocumentNumber && submissions.length > 0 && (
                    <Button
                      color="cap-blue"
                      variant="outline"
                      size="sm"
                      onClick={handleAdjustDateRange}
                    >
                      Align date range
                    </Button>
                  )}
                </div>

                {filterValue && submissions.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                    <Badge color="cap-blue" size="sm" variant="light">
                      Showing {filteredSubmissions.length} of {submissions.length} loaded
                    </Badge>
                    <Button
                      variant="subtle"
                      size="sm"
                      color="gray"
                      onClick={handleClearFilter}
                    >
                      Clear filter
                    </Button>
                  </div>
                )}
              </div>
            )
          )}
          {/* Export Modal */}
          {selectedTemplate && (
            <ExportSubmissionsModal
              open={exportModalOpen}
              onClose={() => setUrlState({ mode: null }, { replace: false })}
              companyId={companyId}
              templateId={selectedTemplate}
              templates={templates}
            />
          )}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "auto",
            paddingRight: "4px",
          }}
        >
          {loading && (
            <Loader
              color="cap-navy"
              style={{ margin: "2em auto", display: "block" }}
            />
          )}
          {isMobile && filterValue && submissions.length > 0 && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "0.5em", padding: "0 4px" }}>
              <Badge color="cap-blue" size="sm" variant="light">
                Showing {filteredSubmissions.length} of {submissions.length} loaded
              </Badge>
              <Button
                variant="subtle"
                size="sm"
                color="gray"
                style={{ minHeight: 36 }}
                onClick={handleClearFilter}
              >
                Clear filter
              </Button>
            </div>
          )}
          {!loading && !submissions.length && (
            <Alert
              title="No entries found"
              color="yellow"
              style={{ marginTop: "1em" }}
            >
              Please adjust your filters or select a different date range.
            </Alert>
          )}
          {!loading && submissions.length > 0 && filteredSubmissions.length === 0 && (
            <Alert
              title="No matching submissions"
              color="yellow"
              style={{ marginTop: "1em" }}
            >
              No submissions match the filter "{filterValue}".
              <Button
                size="sm"
                variant="outline"
                color="cap-navy"
                ml="sm"
                onClick={handleClearFilter}
              >
                Clear filter
              </Button>
            </Alert>
          )}
          {!loading && filteredSubmissions.length > 0 && (
            <>
              <ResponsiveDataTable
                elements={sortedAndFilteredSubmissions}
                sortColumn={sortColumn}
                sortDirection={sortDirection}
                onSort={handleSort}
                verifiedIconRender={(sub) => <VerifiedIcon element={sub} />}
                actionsLabel={isMobile ? "" : "Actions"}
                actionsRender={(sub, isMobileRow) =>
                  isMobileRow ? (
                    <Menu
                      position="bottom"
                      placement="end"
                      shadow="md"
                      size="md"
                      withinPortal
                      styles={{
                        item: {
                          minHeight: 44,
                          padding: "10px 16px",
                          fontSize: 15,
                          fontWeight: 500,
                        },
                        itemIcon: {
                          width: 20,
                          height: 20,
                          marginRight: 12,
                        },
                      }}
                      control={
                        <ActionIcon
                          size={44}
                          aria-label={`Actions for submission ${sub.documentNumber || sub.id}`}
                          variant="outline"
                          color="cap-blue"
                          sx={{ minWidth: 44, minHeight: 44 }}
                        >
                          <IconDotsVertical size={20} />
                        </ActionIcon>
                      }
                    >
                      <Menu.Item
                        icon={<Eye size={16} />}
                        onClick={() => {
                          setDrawerSubmission(sub);
                          openOverlay(sub.id, "view", { replace: false });
                        }}
                      >
                        View
                      </Menu.Item>
                      <Menu.Item
                        icon={<Edit size={16} />}
                        onClick={() => {
                          setDrawerSubmission(sub);
                          openOverlay(sub.id, "edit", { replace: false });
                        }}
                      >
                        Edit
                      </Menu.Item>
                      <Divider />
                      <Menu.Item
                        color="red"
                        icon={<Trash size={16} />}
                        onClick={() => {
                          setDrawerSubmission(sub);
                          openOverlay(sub.id, "delete", { replace: false });
                        }}
                      >
                        Delete
                      </Menu.Item>
                    </Menu>
                  ) : (
                    <Group spacing={6} noWrap>
                      <Tooltip label="View submission" withArrow position="top">
                        <ActionIcon
                          size="sm"
                          color="cap-navy"
                          variant="hover"
                          aria-label={`View submission ${sub.documentNumber || sub.id}`}
                          onClick={() => {
                            setDrawerSubmission(sub);
                            openOverlay(sub.id, "view", { replace: false });
                          }}
                        >
                          <Eye size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Edit submission" withArrow position="top">
                        <ActionIcon
                          size="sm"
                          color="cap-navy"
                          variant="hover"
                          aria-label={`Edit submission ${sub.documentNumber || sub.id}`}
                          onClick={() => {
                            setDrawerSubmission(sub);
                            openOverlay(sub.id, "edit", { replace: false });
                          }}
                        >
                          <Edit size={16} />
                        </ActionIcon>
                      </Tooltip>
                      <Tooltip label="Delete submission" withArrow position="top">
                        <ActionIcon
                          size="sm"
                          color="red"
                          variant="hover"
                          aria-label={`Delete submission ${sub.documentNumber || sub.id}`}
                          onClick={() => {
                            setDrawerSubmission(sub);
                            openOverlay(sub.id, "delete", { replace: false });
                          }}
                        >
                          <Trash size={16} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )
                }
                columns={[
                  { key: "docNumber", label: "Doc #", render: (sub) => sub.documentNumber },
                  { key: "date", label: "Date", render: (sub) => sub.submissionDate ? toTimestampToDateString(sub.submissionDate) : "" },
                  ...displayFields.map((field, idx) => ({
                    key: field.key,
                    label: field.label,
                    render: (sub: any) => (
                      <FieldValueDisplay
                        value={sub.data && sub.data[field.key] ? sub.data[field.key].value : ""}
                        type={field.type}
                        label={field.label}
                        metadata={sub.data?.[field.key]?.metadata || null}
                      />
                    ),
                    hideOnMobile: idx >= 1
                  }))
                ]}
                rowProps={(sub) => ({
                  onClick: () => {
                    setDrawerSubmission(sub);
                    openOverlay(sub.id, "view", { replace: false });
                  },
                  style: { cursor: "pointer" }
                })}
              />
              {submissions.length > 0 && (
                <div
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexWrap: "wrap",
                    gap: "1.5rem",
                    marginTop: "2em",
                    marginBottom: "1em",
                  }}
                >
                  {!paginationDone && (
                    loading && submissions.length > 0 ? (
                      <Loader color="cap-navy" />
                    ) : (
                      <Button
                        color="cap-navy"
                        size={isMobile ? "md" : "sm"}
                        style={{
                          minWidth: isMobile ? "100%" : "160px",
                          minHeight: isMobile ? 44 : undefined,
                          height: isMobile ? 44 : undefined,
                        }}
                        onClick={() => loadMoreEntries(false)}
                        disabled={loading}
                      >
                        Load more entries
                      </Button>
                    )
                  )}
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      width: isMobile ? "100%" : "auto",
                      justifyContent: isMobile ? "center" : undefined,
                    }}
                  >
                    <Text size={isMobile ? "sm" : "xs"} color="dimmed" weight={500}>
                      Page size:
                    </Text>
                    <SegmentedControl
                      size={isMobile ? "md" : "xs"}
                      value={String(pageSize)}
                      onChange={(val) => handlePageSizeChange(Number(val))}
                      data={[
                        { label: "20", value: "20" },
                        { label: "50", value: "50" },
                        { label: "100", value: "100" },
                      ]}
                      styles={{
                        root: {
                          minHeight: isMobile ? 44 : undefined,
                        },
                        control: {
                          minHeight: isMobile ? 44 : undefined,
                          height: isMobile ? 44 : undefined,
                        },
                        label: {
                          minHeight: isMobile ? 44 : undefined,
                          height: isMobile ? 44 : undefined,
                          display: isMobile ? "flex" : undefined,
                          alignItems: isMobile ? "center" : undefined,
                          justifyContent: isMobile ? "center" : undefined,
                          paddingLeft: isMobile ? 14 : undefined,
                          paddingRight: isMobile ? 14 : undefined,
                        },
                      }}
                    />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
      <Drawer
        opened={drawerOpen}
        onClose={() => closeOverlay({ replace: false })}
        title={selectedTemplateObj?.name}
        padding="xl"
        size={isMobile ? "100%" : "800px"}
        position="right"
      >
        <div style={{ height: "100%", paddingBottom: '40px' }}>
          {drawerSubmission && selectedTemplateObj && (
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
              <Text weight={400} mb="md">
                Submitted by:{" "}
                {drawerSubmission.submitterName ||
                  drawerSubmission.submitterEmail ||
                  drawerSubmission.submitterUid}
              </Text>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <Text size="sm" color="dimmed" mb="xs">
                    Date: {toTimestampToDateString(drawerSubmission.submissionDate)}
                  </Text>
                  {drawerSubmission.documentNumber && (
                    <Group spacing={6} align="center" mb="md">
                      <Text size="sm" color="dimmed">
                        Document Number: {drawerSubmission.documentNumber}
                      </Text>
                      <Tooltip label={copiedDocNumber ? "Copied!" : "Copy Document #"} withArrow>
                        <ActionIcon
                          size="xs"
                          aria-label="Copy Document Number"
                          color={copiedDocNumber ? "teal" : "gray"}
                          onClick={() => {
                            navigator.clipboard.writeText(drawerSubmission.documentNumber!);
                            setCopiedDocNumber(true);
                            setTimeout(() => setCopiedDocNumber(false), 1500);
                          }}
                        >
                          {copiedDocNumber ? <Check size={12} color="teal" /> : <Copy size={12} />}
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  )}
                </div>
                <Popover
                  opened={actionsOpened}
                  onClose={() => setActionsOpened(false)}
                  target={
                    <Button
                      color="cap-navy"
                      size="sm"
                      mb="md"
                      variant="outline"
                      leftIcon={<Bolt size={14} />}
                      onClick={() => setActionsOpened((o) => !o)}
                    >
                      Actions
                    </Button>
                  }
                  width={260}
                  position="top"
                  withArrow
                >
                  <div style={{ display: "grid", gap: "12px" }}>
                    {!drawerSubmission.transactionReviewed && (
                      <Button
                        fullWidth
                        size="md"
                        color="cap-green"
                        style={{ minHeight: 44 }}
                        leftIcon={<EyeCheck size={16} />}
                        onClick={() => handleTransactionReviewed()}
                      >
                        Mark as reviewed
                      </Button>
                    )}
                    <PDFExportButton
                      companyId={companyId}
                      templateName={selectedTemplateObj?.name || ""}
                      fields={selectedTemplateObj.fields || []}
                      submission={drawerSubmission}
                      hideNullishInPdf={(selectedTemplateObj as any)?.hideNullishInPdf}
                      pdfLogoMaxWidth={(selectedTemplateObj as any)?.pdfLogoMaxWidth}
                      pdfPhotoMaxWidth={(selectedTemplateObj as any)?.pdfPhotoMaxWidth}
                      pdfSignatureMaxWidth={(selectedTemplateObj as any)?.pdfSignatureMaxWidth}
                      fullWidth
                      size="md"
                      variant="outline"
                      color="cap-blue"
                      style={{ minHeight: 44 }}
                    />
                    <Button
                      fullWidth
                      size="md"
                      variant="outline"
                      color="cap-navy"
                      style={{ minHeight: 44 }}
                      leftIcon={<Edit size={16} />}
                      onClick={() => {
                        setUrlState({ mode: "edit" }, { replace: false });
                        setActionsOpened(false);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      color="red"
                      variant="outline"
                      fullWidth
                      size="md"
                      style={{ minHeight: 44 }}
                      leftIcon={<Trash size={16} />}
                      onClick={() => {
                        setUrlState({ mode: "delete" }, { replace: false });
                        setActionsOpened(false);
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </Popover>
              </div>
              <Divider mb="md" />
              {editMode ? (
                <SubmissionEditForm
                  template={selectedTemplateObj as any}
                  submission={drawerSubmission}
                  selectOptions={selectOptions}
                  onSaveData={async (newData) => {
                    try {
                      const response = await alterTemplateSubmission(drawerSubmission.id, newData, companyId);
                      let updatedSubmission = response?.data || response;
                      if (!updatedSubmission) {
                        const mergedData = { ...(drawerSubmission.data || {}), ...newData };
                        await updateDoc(doc(db, 'flexFormSubmissions', drawerSubmission.id), {
                          data: mergedData,
                          isModified: true,
                        });
                        updatedSubmission = { ...drawerSubmission, data: mergedData, isModified: true };
                      }
                      if (updatedSubmission) {
                        setSubmissions(prev => prev.map(s => s.id === drawerSubmission.id ? updatedSubmission : s));
                        setDrawerSubmission(updatedSubmission);
                        setUrlState({ mode: "view" }, { replace: false });
                        refreshSelectOptions().catch(() => {});
                      }
                    } catch (error) {
                      console.error('Save data failed', error);
                    }
                  }}
                  onSaveSubmissionDate={async (timestamp) => {
                    try {
                      const isoDate =
                        typeof timestamp?.toDate === "function"
                          ? timestamp.toDate().toISOString()
                          : timestamp instanceof Date
                          ? timestamp.toISOString()
                          : new Date(timestamp as any).toISOString();
                      const response = await alterTemplateSubmission(drawerSubmission.id, null, companyId, isoDate);
                      let updatedSubmission = response?.data || response;
                      if (!updatedSubmission) {
                        await updateDoc(doc(db, 'flexFormSubmissions', drawerSubmission.id), {
                          submissionDate: timestamp,
                          isModified: true,
                        });
                        updatedSubmission = { ...drawerSubmission, submissionDate: timestamp, isModified: true };
                      }
                      if (updatedSubmission) {
                        const updatedRecord = { ...drawerSubmission, ...updatedSubmission, submissionDate: timestamp, isModified: true };
                        setSubmissions(prev => prev.map(s => s.id === drawerSubmission.id ? updatedRecord : s));
                        setDrawerSubmission(updatedRecord);
                        setUrlState({ mode: "view" }, { replace: false });
                        refreshSelectOptions().catch(() => {});
                      }
                    } catch (error) {
                      console.error('Saving new date failed', error);
                    }
                  }}
                  onCancel={() => setUrlState({ mode: "view" }, { replace: false })}
                />
              ) : (
                <ScrollArea style={{ flex: 1 }}>
                  <SubmissionFieldDisplayTable
                    fields={selectedTemplateObj.fields || []}
                    data={drawerSubmission.data}
                  />
                </ScrollArea>
              )}
            </div>
          )}
        </div>
      </Drawer>
      <Modal
        opened={deleteModalOpen}
        onClose={() => setUrlState({ mode: "view" }, { replace: false })}
        title="Confirm Deletion"
      >
        <Text size="sm">Deleting this submission will permanently remove it and cannot be undone.</Text>
        <Group position="right" mt="md">
          <Button variant="outline" leftIcon={<ArrowBack size={14} />} onClick={() => setUrlState({ mode: "view" }, { replace: false })}>Cancel</Button>
          <Button color="red" leftIcon={<Trash size={14} />} onClick={handleDelete}>Delete</Button>
        </Group>
      </Modal>
      <Space h="xl" />
    </>
  );
}
